import { gql, useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useLayoutEffect } from 'react';

import { MeasureBundleObject, NudgeObject } from '@grow-therapy-team/seedling-components';

import { Server } from '../apollo/types';
import { logger } from '../datadog/logger';
import { sortMeasureBundles } from './measures/utils';
import {
  patientShortIdToHasCompletedMeasureResponsesAtom,
  patientShortIdToMeasuresFetchFailedAtom,
  patientShortIdWithFetchedMeasuresAtom,
  selectedClientMeasuresData,
  selectedPatientNudgesAtom,
} from './state';

export type GetPatientMeasuresByPatientShortIdQueryData = {
  patientChart?: {
    __typename?: 'PatientObject';
    id: string;
    shortId: string;
    hasCompletedMeasureResponses: boolean | undefined;
    measureBundles: MeasureBundleObject[];
    nudges: NudgeObject[] | null;
  } | null;
};

export type GetPatientMeasuresByPatientShortIdVariables = {
  patientShortId?: string;
};

export const GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID = gql`
  query GetPatientMeasuresByPatientShortId($patientShortId: ID!) {
    patientChart(values: { patientShortId: $patientShortId }) {
      id
      shortId
      hasCompletedMeasureResponses
      measureBundles {
        measureBundleShortId
        ...MeasureBundlesList_MeasureBundleFields
        measureBundleItems {
          measureBundleItemId
          measureSchema {
            measureSchemaShortId
            type
            ...MeasuresContent_MeasureSchemaFields
          }
        }
      }
      nudges {
        id
        nudgeSchemaId
        nudgeSchema {
          trigger
        }
        nudgeAdditionalInfo {
          triggeringMeasureSchemaType
        }
      }
    }
  }
`;

const useSetPatientMeasuresDataCallback = () => {
  const setNudges = useSetAtom(selectedPatientNudgesAtom);
  const setMeasuresData = useSetAtom(selectedClientMeasuresData);
  const setPatientShortIdWithFetchedMeasures = useSetAtom(patientShortIdWithFetchedMeasuresAtom);
  const setPatientShortIdToMeasuresFetchFailed = useSetAtom(patientShortIdToMeasuresFetchFailedAtom);
  const setPatientShortIdToHasCompletedMeasureResponses = useSetAtom(patientShortIdToHasCompletedMeasureResponsesAtom);
  return useCallback(
    (data: GetPatientMeasuresByPatientShortIdQueryData) => {
      const patientChart = data?.patientChart;
      if (!patientChart) return;
      setNudges(patientChart?.nudges ?? []);
      const sortedMeasureData = sortMeasureBundles(patientChart?.measureBundles ?? [], 'createdAt');
      const patientShortId = patientChart?.shortId;
      setPatientShortIdToHasCompletedMeasureResponses((prev) => ({
        ...prev,
        [patientShortId]: patientChart?.hasCompletedMeasureResponses ?? false,
      }));
      setMeasuresData(sortedMeasureData);

      setPatientShortIdToMeasuresFetchFailed((prev) => ({
        ...prev,
        [patientShortId]: false,
      }));
      setPatientShortIdWithFetchedMeasures(patientShortId);
    },
    [
      setNudges,
      setMeasuresData,
      setPatientShortIdToMeasuresFetchFailed,
      setPatientShortIdWithFetchedMeasures,
      setPatientShortIdToHasCompletedMeasureResponses,
    ],
  );
};

/** Writes `GetPatientMeasuresByPatientShortId` results into provider Jotai state (bundles, nudges, flags). */
export function useApplyPatientMeasuresQueryResult() {
  return useSetPatientMeasuresDataCallback();
}

export function useGetMeasuresByPatientShortId(patientShortId?: string) {
  const applyResults = useSetPatientMeasuresDataCallback();
  const setPatientShortIdToMeasuresFetchFailed = useSetAtom(patientShortIdToMeasuresFetchFailedAtom);
  const onCompleted = useCallback(
    (data: GetPatientMeasuresByPatientShortIdQueryData) => {
      if (!data.patientChart) {
        if (patientShortId) {
          setPatientShortIdToMeasuresFetchFailed((prev) => ({
            ...prev,
            [patientShortId]: true,
          }));
        }
        return;
      }
      applyResults(data);
    },
    [applyResults, patientShortId, setPatientShortIdToMeasuresFetchFailed],
  );
  const existingPatientMeasuresWereFetchedFor = useAtomValue(patientShortIdWithFetchedMeasuresAtom);
  const patientMeasuresAlreadyFetched = existingPatientMeasuresWereFetchedFor === patientShortId;
  // TODO CARED-2858 remove skip logic & start returning the query once we've fixed the unnecessary re-fetching of measures
  const { loading } = useQuery<
    GetPatientMeasuresByPatientShortIdQueryData,
    GetPatientMeasuresByPatientShortIdVariables
  >(GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID, {
    context: { server: Server.MONOLITH },
    variables: { patientShortId },
    skip: !patientShortId || patientMeasuresAlreadyFetched,
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      if (patientShortId) {
        setPatientShortIdToMeasuresFetchFailed((prev) => ({
          ...prev,
          [patientShortId]: true,
        }));
      }
      logger.error('Unable to fetch client measures', { patientShortId }, error);
    },
    onCompleted,
  });

  useLayoutEffect(() => {
    if (!patientShortId || !loading) return;

    setPatientShortIdToMeasuresFetchFailed((prev) => {
      if (!prev[patientShortId]) return prev;
      return {
        ...prev,
        [patientShortId]: false,
      };
    });
  }, [loading, patientShortId, setPatientShortIdToMeasuresFetchFailed]);
}

export function useLazyGetMeasuresByPatientShortId() {
  const applyResults = useSetPatientMeasuresDataCallback();
  const apolloClient = useApolloClient();
  const onCompleted = useCallback(
    (data: GetPatientMeasuresByPatientShortIdQueryData) => {
      applyResults(data);
      apolloClient.refetchQueries({ include: ['GetMicClientMeasureCheckInInsights'] }).catch((error) => {
        logger.error('Unable to refetch related measure queries', {}, error);
      });
    },
    [applyResults, apolloClient],
  );
  return useLazyQuery<GetPatientMeasuresByPatientShortIdQueryData, GetPatientMeasuresByPatientShortIdVariables>(
    GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID,
    {
      context: { server: Server.MONOLITH },
      notifyOnNetworkStatusChange: true,
      onCompleted,
    },
  );
}
